/**
 * HUD 씬. PlayScene 위에서 병렬로 돌고 맵 전환 시 재시작되지 않는다.
 */

import Phaser from 'phaser';
import type { AssetTable } from '../assets/loader';
import { Sfx } from '../audio/Sfx';
import { synth } from '../audio/Synth';
import { EV, type DeathPromptPayload, type MapChangedPayload, type NpcTalkPayload, type ShopOpenPayload, type ToastPayload } from '../game/events';
import { TOUCH_EVENT, type TouchUi } from '../platform/TouchPad';
import { toggleFullscreen } from '../platform/desktop';
import type { PlayerState } from '../systems/PlayerState';
import { ChatOverlay } from '../ui/ChatOverlay';
import { FriendOverlay } from '../ui/FriendOverlay';
import { DeathOverlay } from '../ui/DeathOverlay';
import { DialogOverlay } from '../ui/DialogOverlay';
import { EquipmentWindow } from '../ui/EquipmentWindow';
import { HelpOverlay } from '../ui/HelpOverlay';
import { InventoryWindow } from '../ui/InventoryWindow';
import { QuestLogWindow } from '../ui/QuestLogWindow';
import { ShopWindow } from '../ui/ShopWindow';
import { SkillWindow } from '../ui/SkillWindow';
import { StatWindow } from '../ui/StatWindow';
import { TitleWindow } from '../ui/TitleWindow';
import { AchieveWindow } from '../ui/AchieveWindow';
import { SettingsWindow } from '../ui/SettingsWindow';
import { TrainingOverlay } from '../ui/TrainingOverlay';
import { MapEditorOverlay } from '../ui/MapEditorOverlay';
import { training } from '../systems/Training';
import { StatusBar } from '../ui/StatusBar';
import { Toast } from '../ui/Toast';
import { wrap, setFitted } from '../ui/fitText';
import { TEXT_COLOR, UI_FONT } from '../ui/theme';
import { net } from '../net/NetClient';
import { REG } from './BootScene';

export class HudScene extends Phaser.Scene {
  private statusBar!: StatusBar;
  private inventory!: InventoryWindow;
  /** 스모크 테스트가 `hud.stats.visible` 로 읽는다 */
  stats!: StatWindow;
  private skills!: SkillWindow;
  private quests!: QuestLogWindow;
  private dialog!: DialogOverlay;
  private shop!: ShopWindow;
  private gear!: EquipmentWindow;
  private help!: HelpOverlay;
  private death!: DeathOverlay;
  private toast!: Toast;
  private chat!: ChatOverlay;
  private friends!: FriendOverlay;
  private titles!: TitleWindow;
  private settingsWin!: SettingsWindow;
  private achieves!: AchieveWindow;
  private trainingUi!: TrainingOverlay;
  private partyLabel!: Phaser.GameObjects.Text;
  private netLabel!: Phaser.GameObjects.Text;
  private state!: PlayerState;
  private mapLabel!: Phaser.GameObjects.Text;
  private readonly lastKeyAt: Record<string, number> = {};

  constructor() {
    super('hud');
  }

  create(): void {
    this.state = this.registry.get(REG.state) as PlayerState;
    const assets = this.registry.get(REG.assets) as AssetTable;

    this.statusBar = new StatusBar(this, this.state);
    this.inventory = new InventoryWindow(this, this.state, assets);
    this.stats = new StatWindow(this, this.state);
    this.skills = new SkillWindow(this, this.state);
    this.quests = new QuestLogWindow(this, this.state);
    this.dialog = new DialogOverlay(this, this.state);
    this.shop = new ShopWindow(this, this.state);
    this.gear = new EquipmentWindow(this, this.state, assets);
    this.help = new HelpOverlay(this);
    this.death = new DeathOverlay(this, (place) => this.chooseRevive(place));
    this.toast = new Toast(this);
    this.chat = new ChatOverlay(this);
    this.friends = new FriendOverlay(this);
    this.titles = new TitleWindow(this, this.state);
    this.settingsWin = new SettingsWindow(this);
    this.achieves = new AchieveWindow(this, this.state);
    this.trainingUi = new TrainingOverlay(this);

    this.partyLabel = this.add.text(this.scale.width - 18, 14, '', {
      fontFamily: UI_FONT,
      fontSize: '12px',
      color: '#d8c070',
      stroke: '#14141c',
      strokeThickness: 3,
      align: 'right',
    }).setOrigin(1, 0);

    this.netLabel = this.add.text(this.scale.width - 18, 32, net.connected ? `온라인 ${net.name}` : '', {
      fontFamily: UI_FONT,
      fontSize: '12px',
      color: '#8fd0a8',
      stroke: '#14141c',
      strokeThickness: 3,
      align: 'right',
    }).setOrigin(1, 0);
    if (net.connected) setFitted(this.netLabel, `온라인 ${net.name}`, 280, 10);

    this.mapLabel = this.add.text(18, 14, '', {
      fontFamily: UI_FONT,
      fontSize: '14px',
      color: TEXT_COLOR.bright,
      stroke: '#14141c',
      strokeThickness: 4,
      ...wrap(this.scale.width - 340),
    });

    this.add
      .text(18, 36, 'F1 도움말 · S 능력 · E 장비 · K 스킬 · U 칭호 · Q 퀘스트 · I 인벤 · J 도전', {
        fontFamily: UI_FONT,
        fontSize: '12px',
        color: '#d0d8e8',
        stroke: '#14141c',
        strokeThickness: 3,
        ...wrap(this.scale.width - 36),
      })
      .setAlpha(0.85);

    this.add
      .text(18, 54, 'O 로그인 · T 채팅 · L 친구 · P 파티 · F3 설정 · F8 훈련', {
        fontFamily: UI_FONT,
        fontSize: '12px',
        color: '#9eb0c8',
        stroke: '#14141c',
        strokeThickness: 3,
        ...wrap(this.scale.width - 36),
      })
      .setAlpha(0.8);

    this.bindKeys();
    this.bindTouch();

    this.state.events.on(EV.toast, this.onToast, this);
    this.state.events.on(EV.mapChanged, this.onMapChanged, this);
    this.state.events.on(EV.levelUp, this.onLevelUp, this);
    this.state.events.on(EV.deathPrompt, this.onDeathPrompt, this);
    this.state.events.on(EV.playerRespawned, this.onPlayerRespawned, this);
    this.state.events.on(EV.npcTalk, this.onNpcTalk, this);
    this.state.events.on(EV.shopOpen, this.onShopOpen, this);
    net.events.on(EV.toast, this.onToast, this);
    net.events.on(EV.netParty, this.onParty, this);
    net.events.on(EV.netStatus, this.onNetStatus, this);

    // PlayerState 는 씬보다 오래 살기 때문에 씬이 내려갈 때 구독을 모두 정리한다 (TS-0005)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener(TOUCH_EVENT, this.onTouchUi);
      this.state.events.off(EV.toast, this.onToast, this);
      this.state.events.off(EV.mapChanged, this.onMapChanged, this);
      this.state.events.off(EV.levelUp, this.onLevelUp, this);
      this.state.events.off(EV.deathPrompt, this.onDeathPrompt, this);
      this.state.events.off(EV.playerRespawned, this.onPlayerRespawned, this);
      this.state.events.off(EV.npcTalk, this.onNpcTalk, this);
      this.state.events.off(EV.shopOpen, this.onShopOpen, this);
      net.events.off(EV.toast, this.onToast, this);
      net.events.off(EV.netParty, this.onParty, this);
      net.events.off(EV.netStatus, this.onNetStatus, this);
      this.statusBar.destroy();
      this.inventory.destroy();
      this.stats.destroy();
      this.skills.close();
      this.quests.close();
      this.dialog.close();
      this.shop.close();
      this.gear.close();
      this.chat.close();
      this.friends.close();
      this.titles.close();
      this.settingsWin.destroy();
      this.achieves.destroy();
      this.trainingUi.destroy();
    });
  }

  private onToast(p: ToastPayload): void {
    this.toast.show(p);
  }

  private onMapChanged(p: MapChangedPayload): void {
    this.mapLabel.setText(p.name);
    setFitted(this.mapLabel, p.name, this.scale.width - 340, 11);
  }

  private onLevelUp(p: { level: number; apGained?: number }): void {
    const ap = p.apGained ?? 0;
    const extra = [ap > 0 ? `AP +${ap}` : '', (p as { spGained?: number }).spGained ? `SP +${(p as { spGained?: number }).spGained}` : '']
      .filter(Boolean)
      .join(' ');
    this.toast.show({ message: extra ? `레벨 ${p.level} 달성!  ${extra}` : `레벨 ${p.level} 달성!`, tone: 'good' });
  }

  private onDeathPrompt(p: DeathPromptPayload): void {
    this.inventory.close();
    this.stats.close();
    this.skills.close();
    this.quests.close();
    this.dialog.close();
    this.shop.close();
    this.gear.close();
    this.help.close();
    this.titles.close();
    this.chat.close();
    this.friends.close();
    this.death.show(p.lostExp, p.exp, p.need);
  }

  private onPlayerRespawned(): void {
    this.death.hide();
  }

  private readonly onTouchUi = (ev: Event): void => {
    const ui = (ev as CustomEvent<TouchUi>).detail;
    if (ui === 'start' || ui === 'create' || ui === 'login') return;
    if (ui === 'inventory') {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.inventory.toggle();
      return;
    }
    if (ui === 'stats') {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.stats.toggle();
      return;
    }
    if (ui === 'skills') {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.skills.toggle();
      return;
    }
    if (ui === 'gear') {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.gear.toggle();
      return;
    }
    if (ui === 'quests') {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.quests.toggle();
      return;
    }
    if (ui === 'titles') {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.titles.toggle();
      return;
    }
    if (ui === 'help') {
      if (this.death.visible) return;
      Sfx.uiClick();
      this.help.toggle();
      return;
    }
    if (ui === 'mute') {
      const muted = synth.toggleMute();
      this.toast.show({ message: muted ? '소리 끔' : '소리 켬', tone: 'info' });
      return;
    }
    if (ui === 'hp') {
      if (this.death.visible) {
        this.chooseRevive('here');
        return;
      }
      this.quickPotion('hp');
      return;
    }
    if (ui === 'mp') {
      if (this.death.visible) {
        this.chooseRevive('town');
        return;
      }
      this.quickPotion('mp');
      return;
    }
    if (ui === 'chat') {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      this.chat.toggle();
      return;
    }
    if (ui === 'revive-here') {
      this.chooseRevive('here');
      return;
    }
    if (ui === 'revive-town') {
      this.chooseRevive('town');
      return;
    }
    if (ui === 'close') {
      if (this.death.visible) return;
      if (this.chat.visible) {
        this.chat.close();
        return;
      }
      if (this.friends.visible) {
        this.friends.close();
        return;
      }
      if (this.dialog.visible) this.dialog.close();
      else if (this.shop.visible) this.shop.close();
      else if (this.inventory.visible) this.inventory.close();
      else if (this.stats.visible) this.stats.close();
      else if (this.skills.visible) this.skills.close();
      else if (this.quests.visible) this.quests.close();
      else if (this.gear.visible) this.gear.close();
      else if (this.titles.visible) this.titles.close();
      else if (this.settingsWin.visible) this.settingsWin.close();
      else if (this.achieves.visible) this.achieves.close();
      else if (this.help.visible) this.help.close();
    }
  };

  private bindTouch(): void {
    window.addEventListener(TOUCH_EVENT, this.onTouchUi);
  }

  /**
   * UI 토글은 폴링(JustDown) 대신 keydown 이벤트로 처리한다.
   * 브라우저가 keyup 을 놓치면(포커스 이동, DevTools 등) JustDown 이 다시 발생하지 않아
   * 창이 닫히지 않는 문제가 있었다. docs/troubleshooting.md TS-0004 참고.
   */
  private bindKeys(): void {
    const kb = this.input.keyboard;
    if (!kb) return;

    const on = (event: string, handler: () => void): void => {
      kb.on(event, () => {
        // 키 반복(auto repeat)으로 두 번 토글되지 않도록 디바운스
        const now = this.time.now;
        if (now - (this.lastKeyAt[event] ?? -999) < 200) return;
        this.lastKeyAt[event] = now;
        handler();
      });
    };

    on('keydown-I', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.inventory.toggle();
    });
    on('keydown-S', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.stats.toggle();
    });
    on('keydown-K', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.skills.toggle();
    });
    on('keydown-E', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.gear.toggle();
    });
    on('keydown-Q', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.quests.toggle();
    });
    on('keydown-U', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.titles.toggle();
    });
    on('keydown-J', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      Sfx.uiClick();
      this.achieves.toggle();
    });
    on('keydown-F3', () => {
      if (this.death.visible) return;
      Sfx.uiClick();
      this.settingsWin.toggle();
    });
    on('keydown-F8', () => {
      training.toggle();
      this.toast.show({ message: training.on ? '트레이닝 켜짐' : '트레이닝 꺼짐', tone: 'info' });
    });
    on('keydown-G', () => {
      if (!training.on) return;
      const on = training.toggleGod();
      this.toast.show({ message: on ? '무적 켜짐' : '무적 꺼짐', tone: 'info' });
    });
    on('keydown-F4', () => {
      MapEditorOverlay.show();
    });
    on('keydown-F7', () => {
      const play = this.scene.get('play') as { toggleRecord?: () => void };
      play.toggleRecord?.();
    });
    on('keydown-F6', () => {
      const play = this.scene.get('play') as { playLast?: () => void };
      play.playLast?.();
    });
    on('keydown-L', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      if (!net.connected) {
        this.toast.show({ message: '온라인이 아닙니다', tone: 'warn' });
        return;
      }
      Sfx.uiClick();
      this.friends.toggle();
    });
    on('keydown-ESC', () => {
      if (this.death.visible) return;
      if (this.chat.visible) {
        this.chat.close();
        return;
      }
      if (this.friends.visible) {
        this.friends.close();
        return;
      }
      if (this.dialog.visible) this.dialog.close();
      else if (this.shop.visible) this.shop.close();
      else if (this.inventory.visible) this.inventory.close();
      else if (this.stats.visible) this.stats.close();
      else if (this.skills.visible) this.skills.close();
      else if (this.quests.visible) this.quests.close();
      else if (this.gear.visible) this.gear.close();
      else if (this.titles.visible) this.titles.close();
      else if (this.settingsWin.visible) this.settingsWin.close();
      else if (this.achieves.visible) this.achieves.close();
      else if (this.help.visible) this.help.close();
    });
    const toggleHelp = (): void => {
      if (this.death.visible) return;
      Sfx.uiClick();
      this.help.toggle();
    };
    on('keydown-F1', toggleHelp);
    on('keydown-H', toggleHelp);
    on('keydown-F11', () => {
      void toggleFullscreen().then((full) =>
        this.toast.show({ message: full ? '전체화면' : '창 모드', tone: 'info' }),
      );
    });
    on('keydown-M', () => {
      const muted = synth.toggleMute();
      this.toast.show({ message: muted ? '소리 끔' : '소리 켬', tone: 'info' });
    });
    on('keydown-ONE', () => {
      if (this.death.visible) {
        this.chooseRevive('here');
        return;
      }
      if (this.dialog.handleKey('ONE')) return;
      this.quickPotion('hp');
    });
    on('keydown-TWO', () => {
      if (this.death.visible) {
        this.chooseRevive('town');
        return;
      }
      if (this.dialog.handleKey('TWO')) return;
      this.quickPotion('mp');
    });
    on('keydown-THREE', () => {
      this.dialog.handleKey('THREE');
    });
    on('keydown-ENTER', () => {
      if (this.chat.visible) return;
      if (this.dialog.handleKey('ENTER')) return;
      if (this.death.visible || this.shop.visible) return;
      this.chat.toggle();
    });
    on('keydown-T', () => {
      if (this.death.visible || this.dialog.visible || this.shop.visible) return;
      this.chat.toggle();
    });
    on('keydown-SPACE', () => {
      this.dialog.handleKey('SPACE');
    });
    on('keydown-Y', () => {
      this.dialog.handleKey('Y');
    });
    on('keydown-N', () => {
      this.dialog.handleKey('N');
    });
  }

  private onNpcTalk(p: NpcTalkPayload): void {
    if (this.death.visible) return;
    this.inventory.close();
    this.stats.close();
    this.skills.close();
    this.quests.close();
    this.shop.close();
    this.gear.close();
    this.help.close();
    Sfx.uiClick();
    this.dialog.open(p.npcId);
  }

  private onShopOpen(p: ShopOpenPayload): void {
    if (this.death.visible) return;
    this.dialog.close();
    this.shop.open(p.shopId);
  }

  private onParty(p: { leader: string; members: { id: string; name: string }[] }): void {
    if (!p.members.length) {
      this.partyLabel.setText('');
      return;
    }
    this.partyLabel.setText(`파티 ${p.members.map((m) => m.name).join(', ')}`);
    setFitted(this.partyLabel, this.partyLabel.text, 280, 10);
  }

  private onNetStatus(p: { connected: boolean; name?: string }): void {
    this.netLabel.setText(p.connected ? `온라인 ${p.name ?? net.name}` : '');
    if (p.connected) setFitted(this.netLabel, this.netLabel.text, 280, 10);
  }

  isDialogOpen(): boolean {
    return this.dialog.visible;
  }

  dialogKey(code: string): boolean {
    return this.dialog.handleKey(code);
  }

  private chooseRevive(place: 'here' | 'town'): void {
    if (!this.death.visible) return;
    Sfx.uiClick();
    this.death.hide();
    this.state.events.emit(EV.reviveChosen, { place });
  }

  override update(_time: number, delta: number): void {
    this.statusBar.update(delta);
    this.trainingUi.refresh();
  }

  private quickPotion(kind: 'hp' | 'mp'): void {
    const found = this.state.inventory.findConsumable(kind);
    if (!found) {
      Sfx.deny();
      this.toast.show({ message: `${kind === 'hp' ? 'HP' : 'MP'} 포션이 없습니다`, tone: 'warn' });
      return;
    }
    if (this.state.useItem(found.tab, found.index)) Sfx.potion();
  }
}
